import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { AtlDialogProps } from './dialog.types.ts';
import { getGlobalStyleSheets } from '../../global-styles';
import { enumAttributeConverter } from '../enum-types.ts';
import {
   DialogSizes,
   DialogSizesValues,
   DialogEvents,
   DialogTags
} from './dialog.types.ts';
import { ifDefined } from 'lit/directives/if-defined.js';
import { JustifyContent, JustifyContentValues } from '../justify-content.ts';

import { AtlantisEvent } from '../helpers.ts';

type OffCanvasType = {
   show: () => void;
   toggle: () => void;
   hide: () => void;
   _focustrap: {
      deactivate: () => void;
   };
};

@customElement(DialogTags.Dialog)
export class AtlDialog extends LitElement implements AtlDialogProps {
   declare show: boolean;
   declare renderCustomHeader?: boolean;
   declare dialogTitle?: string;
   declare displayCloseButton?: boolean;
   declare size?: DialogSizes;
   declare static?: boolean;
   declare backdrop?: boolean;
   declare keyboard?: boolean;
   declare bodyScrolling?: boolean;
   private _dialogModal: OffCanvasType;

   declare footerJustifyContent?: JustifyContent;

   @query('.modal')
   private _dialog?: HTMLElement;

   @query('.modal-content')
   private _dialogContent?: HTMLElement;

   private _addedEventListeners = false;

   static properties = {
      show: { type: Boolean },
      renderCustomHeader: { type: Boolean, state: true },
      dialogTitle: { type: String },
      displayCloseButton: { attribute: 'display-close-button', type: Boolean },
      size: { type: String, converter: enumAttributeConverter(DialogSizes) },
      static: { type: Boolean },
      backdrop: { type: Boolean },
      keyboard: { type: Boolean },
      bodyScrolling: { type: Boolean },
      footerJustifyContent: {
         attribute: 'footer-justify-content',
         type: String,
         converter: enumAttributeConverter(JustifyContent),
      },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .show {
            display: block;
         }
      `,
   ];

   protected firstUpdated(_changedProperties: PropertyValues): void {
      super.firstUpdated(_changedProperties);

      this._dialog?.addEventListener(
         'hide.bs.offcanvas',
         this._dispatchHideDialogEvent
      );
      this._dialog?.addEventListener(
         'hidden.bs.offcanvas',
         this._dispatchHiddenDialogEvent
      );
      this._dialog?.addEventListener(
         'hidePrevented.bs.offcanvas',
         this._dispatchBlockedDialogEvent
      );
      this._dialog?.addEventListener(
         'show.bs.offcanvas',
         this._dispatchOpenDialogEvent
      );
      this._dialog?.addEventListener(
         'shown.bs.offcanvas',
         this._dispatchOpenedDialogEvent
      );

      this._dialogModal = new bootstrap.Offcanvas(this._dialog);
   }

   override disconnectedCallback() {
      document.removeEventListener('click', this._handleBackdropClickEvent, {
         capture: true,
      });
      this._addedEventListeners = false;

      this._dialog?.removeEventListener(
         'shown.bs.offcanvas',
         this._dispatchOpenedDialogEvent
      );
      this._dialog?.removeEventListener(
         'show.bs.offcanvas',
         this._dispatchOpenDialogEvent
      );
      this._dialog?.removeEventListener(
         'hidePrevented.bs.offcanvas',
         this._dispatchBlockedDialogEvent
      );

      this._dialog?.removeEventListener(
         'hidden.bs.offcanvas',
         this._dispatchHiddenDialogEvent
      );
      this._dialog?.removeEventListener(
         'hide.bs.offcanvas',
         this._dispatchHideDialogEvent
      );

      this._dialogModal?.hide();

      super.disconnectedCallback();
   }

   protected override updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);

      if (changedProperties.has('show')) {
         if (this._dialogModal && this.show) {
            this._dialogModal.show();

            setTimeout(() => {
               if (!this._addedEventListeners) {
                  document.addEventListener(
                     'click',
                     this._handleBackdropClickEvent,
                     { capture: true }
                  );
                  this._addedEventListeners = true;
               }
            }, 100);
         } else if (this._dialogModal) {
            this._dialogModal.hide();
            if (this._addedEventListeners) {
               document.removeEventListener(
                  'click',
                  this._handleBackdropClickEvent,
                  { capture: true }
               );
               this._addedEventListeners = false;
            }
         }
      }
    }

   private _dispatchHideDialogEvent = () => {
      this._dispatchDialogEvent(DialogEvents.DialogClosing);
   };
   private _dispatchHiddenDialogEvent = () => {
      this._dispatchDialogEvent(DialogEvents.DialogClosed);
   };
   private _dispatchOpenDialogEvent = () =>
      this._dispatchDialogEvent(DialogEvents.DialogOpen);
   private _dispatchOpenedDialogEvent = () => {
      this._dialogModal?._focustrap.deactivate();
      this._dialog?.focus();
      this._dispatchDialogEvent(DialogEvents.DialogOpened);
   };
   private _dispatchBlockedDialogEvent = () =>
      this._dispatchDialogEvent(DialogEvents.DialogBlocked);

   private _dispatchDialogEvent = (eventName: DialogEvents) => {
      this.dispatchEvent(
         new Event(eventName, { bubbles: true, composed: true })
      );
   };

   private _handleHideModal = () => {
      this._dialogModal?.hide();
   };

   private _handleBackdropClickEvent = (e: MouseEvent) => {
      if (!this.show) return;

      const path = e.composedPath();

      const isDialogClicked =
         this._dialogContent && path.includes(this._dialogContent);
      if (isDialogClicked) return;

      if (this.static) {
         this._dispatchBlockedDialogEvent();
      } else if (this.backdrop) {
         this._dialogModal?.hide();
      }
   };

   private _renderCloseButton() {
      if (this.displayCloseButton) {
         return html`<button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click=${this._handleHideModal}
         ></button>`;
      }
      return nothing;
   }

   private _renderTitle() {
      if (this.dialogTitle != null && this.dialogTitle != '') {
         return html`
            <h2 class="modal-title dialog-title">${this.dialogTitle}</h2>
         `;
      }
      return nothing;
    }

    private _handleHeaderSlotChange(event: AtlantisEvent<Event, HTMLSlotElement>) {
        const childNodes = event.target.assignedNodes({ flatten: true });
        this.renderCustomHeader = childNodes.length > 0;
    }

    private _renderModalHeader() {
        if (this.renderCustomHeader || this.dialogTitle || this.displayCloseButton) {
            return html`
            <div class="modal-header">
                <slot name="dialog-header" @slotchange="${this._handleHeaderSlotChange}">
                    ${this._renderTitle()}
                    ${this._renderCloseButton()}
                </slot>
            </div>`;
        } else {
            return html`<slot @slotchange="${this._handleHeaderSlotChange}" name="dialog-header"></slot>`
        }
   }

   private _renderModalFooter = () => {
      const footerClass = ['modal-footer'];
      if (this.footerJustifyContent) {
         footerClass.push(JustifyContentValues[this.footerJustifyContent]);
      }
      return html`
         <div class="${footerClass.join(' ')}">
            <slot name="dialogFooter"></slot>
         </div>
      `;
   };

   render() {
      const dialogClass = ['modal'];
      if (this.className) {
         dialogClass.push(this.className);
      }
      const mainClass = [
         'modal-dialog',
         'modal-dialog-centered',
         'app-dialog',
         `modal-${DialogSizesValues[this.size ?? DialogSizes.Medium]}`,
      ];

      return html`
         <div
            tabindex="-1"
            role="dialog"
            class="${dialogClass.join(' ')}"
            data-bs-scroll=${this.bodyScrolling ? 'true' : nothing}
            data-bs-backdrop=${this.static
               ? 'static'
               : this.backdrop
                 ? 'true'
                 : 'false'}
            data-bs-keyboard=${this.keyboard ? 'true' : 'false'}
            aria-label=${ifDefined(this.dialogTitle)}
            aria-modal="true"
         >
            <div role="main" class="${mainClass.join(' ')}">
               <div class="modal-content">
                  ${this._renderModalHeader()}
                  <div
                     class="modal-body component-custom-vertical-scrollbar-bold"
                  >
                     <slot name="dialogBody"></slot>
                  </div>
                  ${this._renderModalFooter()}
               </div>
            </div>
         </div>
      `;
   }
}
